import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  BusinessMoreDetails,
  BusinessProfile,
  BusinessRatingsSummary,
} from "../../interfaces/BusinessObject";
import { ProductDetailsModal } from "../../interfaces/ProductObject";
import { MediaRecord } from "../../interfaces/MediaObject";
import { resetAllState } from "./resetAllState";
import {
  fetchMyBusinesses,
  fetchBusinessById,
  fetchBusinessListings,
  createBusiness,
  updateBusiness,
  deleteBusiness,
  updateBusinessMoreDetails,
  fetchBusinessCustomers,
  fetchBusinessRatings,
  submitBusinessRating,
  deleteBusinessRating,
} from "../../services/thunks/BusinessThunk";

interface BusinessState {
  myBusinesses: BusinessProfile[];
  currentBusiness: BusinessProfile | null;
  /** businessId whose fetchBusinessById last failed (errorMap is not per-id). */
  failedBusinessId: string | null;
  ratingsSummary: BusinessRatingsSummary | null;
  /** Customer phone numbers of the business being viewed (owner/collaborator only). */
  customers: string[];
  loadingMap: Record<string, string | null>;
  errorMap: Record<string, string | null>;
  successMap: Record<string, boolean>;
  currentBusinessProducts: ProductDetailsModal[];
  currentBusinessTotalCount: number;
  currentBusinessGallery: MediaRecord[] | null;
}

const initialState: BusinessState = {
  myBusinesses: [],
  currentBusiness: null,
  failedBusinessId: null,
  ratingsSummary: null,
  customers: [],
  loadingMap: {},
  errorMap: {},
  successMap: {},
  currentBusinessProducts: [],
  currentBusinessTotalCount: 0,
  currentBusinessGallery: null,
};

const businessSlice = createSlice({
  name: "business",
  initialState,
  reducers: {
    setMyBusinesses(state, action: PayloadAction<BusinessProfile[]>) {
      state.myBusinesses = action.payload;
    },
    setCurrentBusiness(state, action: PayloadAction<BusinessProfile>) {
      state.currentBusiness = action.payload;
    },
    /** Replaces one entry of myBusinesses in place (no-op if absent) — keeps the
     *  user's business list consistent after a single-business update such as
     *  owner sign-off (isOnboarded), without a full refetch. */
    syncMyBusiness(state, action: PayloadAction<BusinessProfile>) {
      state.myBusinesses = state.myBusinesses.map((b) =>
        b.businessId === action.payload.businessId ? { ...b, ...action.payload } : b
      );
    },
    clearCurrentBusiness(state) {
      state.currentBusiness = null;
    },
    clearRatingsSummary(state) {
      state.ratingsSummary = null;
    },
    clearCustomers(state) {
      state.customers = [];
    },
    setCurrentBusinessProducts(state, action: PayloadAction<ProductDetailsModal[]>) {
      state.currentBusinessProducts = action.payload;
    },
    setCurrentBusinessTotalCount(state, action: PayloadAction<number>) {
      state.currentBusinessTotalCount = action.payload;
    },
    clearCurrentBusinessProducts(state) {
      state.currentBusinessProducts = [];
      state.currentBusinessTotalCount = 0;
    },
    setCurrentBusinessGallery(state, action: PayloadAction<MediaRecord[]>) {
      state.currentBusinessGallery = action.payload;
    },
    clearCurrentBusinessGallery(state) {
      state.currentBusinessGallery = null;
    },
  },
  extraReducers: (builder) => {
    // fetchMyBusinesses
    builder
      .addCase(fetchMyBusinesses.pending, (state) => {
        state.loadingMap.fetchMyBusinesses = "Loading your businesses...";
        state.errorMap.fetchMyBusinesses = null;
        state.successMap.fetchMyBusinesses = false;
      })
      .addCase(fetchMyBusinesses.fulfilled, (state, action) => {
        state.loadingMap.fetchMyBusinesses = null;
        state.successMap.fetchMyBusinesses = true;
        state.myBusinesses = action.payload;
      })
      .addCase(fetchMyBusinesses.rejected, (state, action) => {
        state.loadingMap.fetchMyBusinesses = null;
        state.errorMap.fetchMyBusinesses = action.payload as string;
      });

    // fetchBusinessById
    builder
      .addCase(fetchBusinessById.pending, (state) => {
        state.loadingMap.fetchBusinessById = "Loading business...";
        state.errorMap.fetchBusinessById = null;
        state.successMap.fetchBusinessById = false;
        state.failedBusinessId = null;
      })
      .addCase(fetchBusinessById.fulfilled, (state, action) => {
        state.loadingMap.fetchBusinessById = null;
        state.successMap.fetchBusinessById = true;
        state.currentBusiness = action.payload;
      })
      .addCase(fetchBusinessById.rejected, (state, action) => {
        state.loadingMap.fetchBusinessById = null;
        state.errorMap.fetchBusinessById = action.payload as string;
        state.failedBusinessId = action.meta.arg;
      });

    // fetchBusinessListings
    builder
      .addCase(fetchBusinessListings.pending, (state) => {
        state.loadingMap.fetchBusinessListings = "Loading listings...";
        state.errorMap.fetchBusinessListings = null;
        state.successMap.fetchBusinessListings = false;
      })
      .addCase(fetchBusinessListings.fulfilled, (state) => {
        state.loadingMap.fetchBusinessListings = null;
        state.successMap.fetchBusinessListings = true;
      })
      .addCase(fetchBusinessListings.rejected, (state, action) => {
        state.loadingMap.fetchBusinessListings = null;
        state.errorMap.fetchBusinessListings = action.payload as string;
      });

    // createBusiness
    builder
      .addCase(createBusiness.pending, (state) => {
        state.loadingMap.createBusiness = "Creating business...";
        state.errorMap.createBusiness = null;
        state.successMap.createBusiness = false;
      })
      .addCase(createBusiness.fulfilled, (state, action) => {
        state.loadingMap.createBusiness = null;
        state.successMap.createBusiness = true;
        state.myBusinesses = [...state.myBusinesses, action.payload];
      })
      .addCase(createBusiness.rejected, (state, action) => {
        state.loadingMap.createBusiness = null;
        state.errorMap.createBusiness = action.payload as string;
      });

    // updateBusiness
    builder
      .addCase(updateBusiness.pending, (state) => {
        state.loadingMap.updateBusiness = "Saving changes...";
        state.errorMap.updateBusiness = null;
        state.successMap.updateBusiness = false;
      })
      .addCase(updateBusiness.fulfilled, (state, action) => {
        state.loadingMap.updateBusiness = null;
        state.successMap.updateBusiness = true;
        state.myBusinesses = state.myBusinesses.map((b) =>
          b.businessId === action.payload.businessId ? action.payload : b
        );
        if (state.currentBusiness?.businessId === action.payload.businessId) {
          state.currentBusiness = action.payload;
        }
      })
      .addCase(updateBusiness.rejected, (state, action) => {
        state.loadingMap.updateBusiness = null;
        state.errorMap.updateBusiness = action.payload as string;
      });

    // deleteBusiness
    builder
      .addCase(deleteBusiness.pending, (state) => {
        state.loadingMap.deleteBusiness = "Deleting business...";
        state.errorMap.deleteBusiness = null;
        state.successMap.deleteBusiness = false;
      })
      .addCase(deleteBusiness.fulfilled, (state, action) => {
        state.loadingMap.deleteBusiness = null;
        state.successMap.deleteBusiness = true;
        const businessId = action.payload;
        state.myBusinesses = state.myBusinesses.filter((b) => b.businessId !== businessId);
        if (state.currentBusiness?.businessId === businessId) {
          state.currentBusiness = null;
        }
      })
      .addCase(deleteBusiness.rejected, (state, action) => {
        state.loadingMap.deleteBusiness = null;
        state.errorMap.deleteBusiness = action.payload as string;
      });

    // updateBusinessMoreDetails
    builder
      .addCase(updateBusinessMoreDetails.pending, (state) => {
        state.loadingMap.updateBusinessMoreDetails = "Saving details...";
        state.errorMap.updateBusinessMoreDetails = null;
        state.successMap.updateBusinessMoreDetails = false;
      })
      .addCase(updateBusinessMoreDetails.fulfilled, (state, action) => {
        state.loadingMap.updateBusinessMoreDetails = null;
        state.successMap.updateBusinessMoreDetails = true;
        const { businessId, moreDetails } = action.payload;
        state.myBusinesses = state.myBusinesses.map((b) =>
          b.businessId === businessId ? { ...b, moreDetails } : b
        );
        if (state.currentBusiness?.businessId === businessId) {
          state.currentBusiness = { ...state.currentBusiness, moreDetails };
        }
      })
      .addCase(updateBusinessMoreDetails.rejected, (state, action) => {
        state.loadingMap.updateBusinessMoreDetails = null;
        state.errorMap.updateBusinessMoreDetails = action.payload as string;
      });

    // fetchBusinessCustomers
    builder
      .addCase(fetchBusinessCustomers.pending, (state) => {
        state.loadingMap.fetchBusinessCustomers = "Loading customers...";
        state.errorMap.fetchBusinessCustomers = null;
        state.successMap.fetchBusinessCustomers = false;
      })
      .addCase(fetchBusinessCustomers.fulfilled, (state, action) => {
        state.loadingMap.fetchBusinessCustomers = null;
        state.successMap.fetchBusinessCustomers = true;
        state.customers = action.payload;
      })
      .addCase(fetchBusinessCustomers.rejected, (state, action) => {
        state.loadingMap.fetchBusinessCustomers = null;
        state.errorMap.fetchBusinessCustomers = action.payload as string;
      });

    // fetchBusinessRatings
    builder
      .addCase(fetchBusinessRatings.pending, (state) => {
        state.loadingMap.fetchBusinessRatings = "Loading reviews...";
        state.errorMap.fetchBusinessRatings = null;
        state.successMap.fetchBusinessRatings = false;
      })
      .addCase(fetchBusinessRatings.fulfilled, (state, action) => {
        state.loadingMap.fetchBusinessRatings = null;
        state.successMap.fetchBusinessRatings = true;
        state.ratingsSummary = action.payload;
      })
      .addCase(fetchBusinessRatings.rejected, (state, action) => {
        state.loadingMap.fetchBusinessRatings = null;
        state.errorMap.fetchBusinessRatings = action.payload as string;
      });

    // submitBusinessRating
    builder
      .addCase(submitBusinessRating.pending, (state) => {
        state.loadingMap.submitBusinessRating = "Submitting your rating...";
        state.errorMap.submitBusinessRating = null;
        state.successMap.submitBusinessRating = false;
      })
      .addCase(submitBusinessRating.fulfilled, (state) => {
        state.loadingMap.submitBusinessRating = null;
        state.successMap.submitBusinessRating = true;
        // Summary is refreshed by the thunk's dispatched fetchBusinessRatings.
      })
      .addCase(submitBusinessRating.rejected, (state, action) => {
        state.loadingMap.submitBusinessRating = null;
        state.errorMap.submitBusinessRating = action.payload as string;
      });

    // deleteBusinessRating
    builder
      .addCase(deleteBusinessRating.pending, (state) => {
        state.loadingMap.deleteBusinessRating = "Deleting your rating...";
        state.errorMap.deleteBusinessRating = null;
        state.successMap.deleteBusinessRating = false;
      })
      .addCase(deleteBusinessRating.fulfilled, (state) => {
        state.loadingMap.deleteBusinessRating = null;
        state.successMap.deleteBusinessRating = true;
      })
      .addCase(deleteBusinessRating.rejected, (state, action) => {
        state.loadingMap.deleteBusinessRating = null;
        state.errorMap.deleteBusinessRating = action.payload as string;
      });

    builder.addCase(resetAllState, () => initialState);
  },
});

export const {
  setMyBusinesses,
  setCurrentBusiness,
  syncMyBusiness,
  clearCurrentBusiness,
  clearRatingsSummary,
  clearCustomers,
  setCurrentBusinessProducts,
  setCurrentBusinessTotalCount,
  clearCurrentBusinessProducts,
  setCurrentBusinessGallery,
  clearCurrentBusinessGallery,
} = businessSlice.actions;

export default businessSlice.reducer;
